import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { PRO_TIER_NAME } from '../lib/productTier';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { isNativeApp } from '../lib/platform';

function parseErr(e: unknown): string {
  if (e instanceof ApiError && e.body) {
    try {
      const j = JSON.parse(e.body) as { error?: string };
      if (j?.error) return j.error;
    } catch {
      /* ignore */
    }
  }
  return e instanceof Error ? e.message : 'Verification failed.';
}

export function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const plan = searchParams.get('plan') === 'adfree' ? 'adfree' : 'coach';
  const [message, setMessage] = useState(
    plan === 'adfree' ? 'Confirming ad-free subscription…' : 'Confirming your subscription…',
  );
  const [done, setDone] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id')?.trim();
    if (!sessionId) {
      setMessage('Missing checkout session. Return to Profile or Coach to try again.');
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        await api<{ ok: boolean }>('/api/stripe/complete-checkout', {
          method: 'POST',
          body: JSON.stringify({ sessionId, plan }),
        });
        if (cancelled) return;
        await refresh();
        if (isNativeApp()) {
          navigate(plan === 'adfree' ? '/profile#ad-free' : '/coach', { replace: true });
        } else {
          setDone(true);
          setMessage('Subscription confirmed. Download the iOS app and sign in with this account.');
        }
      } catch (e) {
        if (!cancelled) setMessage(parseErr(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, refresh, navigate, plan]);

  return (
    <>
      <PageHeader
        title="Payment received"
        subtitle={
          plan === 'adfree'
            ? 'Activating ad-free…'
            : `Activating ${PRO_TIER_NAME}…`
        }
      />
      <div className="flex min-h-[30vh] flex-col items-center justify-center px-4 text-center">
        <p className="max-w-lg text-sm leading-relaxed text-zinc-400">{message}</p>
        {done ? (
          <div className="mt-6 flex flex-col items-center gap-3">
            <Link
              to="/download"
              className="rounded-full bg-sky-400 px-5 py-2.5 text-sm font-semibold text-zinc-950"
            >
              Get the app
            </Link>
            <Link to="/account" className="text-sm font-semibold text-sky-400">
              Manage billing
            </Link>
          </div>
        ) : null}
      </div>
    </>
  );
}
